/**
 * Decision-brief adapter for the Evaluate drilldown.
 *
 * Single source for everything the Decision Brief renders. The demo fixture
 * (demoOpportunities.ts) carries only description / roiEstimate / timeToValue /
 * implementationEffort / keyRisks, so verdict, scores, narrative, structured risks and
 * roadmap are derived here and layered with authored copy from ./briefOverrides.
 *
 * Layering order (lowest → highest precedence):
 *   1. deriveBrief(opp, rank)  — template synthesis from real fixture fields
 *   2. BRIEF_OVERRIDES[opp.id] — authored copy ported from the design handoff
 *
 * Pages must consume getDecisionBrief() only, never the two layers directly, so a backend
 * that supplies real verdicts/scores/rationale can replace them without touching UI.
 *
 * TODO: verdict, decisionText and the value/implementation scores should come from the
 * scoring model rather than the rank-derived thresholds below. When that lands, replace
 * the derive* helpers and drop the corresponding override fields.
 */

import type { Opportunity } from './mockOpportunities';
import { DEMO_OPPORTUNITIES } from './mockOpportunities';
import { BRIEF_OVERRIDES, type RoadmapPhase } from './briefOverrides';
import { isQuickWin } from '../config/opportunityBadge';
import { roiForUseCase } from '../roi/useCaseRoi';
import { hubFigures } from '../roi/hubFigures';
import { OPP_TO_UC, scoreForUseCase } from './adapters/stage300FeaturedContent';
import type { VerdictKey } from '../theme/decisionTokens';
import type { RiskItem } from '../components/RiskCard';

export type { RoadmapPhase };

export type EffortTag = 'Quick Win' | 'Moderate';

/**
 * The only fixture fields a brief is derived from. Deliberately narrower than the full
 * Opportunity so callers holding a page-local variant (EAOpportunitiesPage reshapes
 * surfacedReasons/assumptions) can pass their records straight in.
 */
export type BriefSource = Pick<
  Opportunity,
  'id' | 'title' | 'category' | 'description' | 'roiEstimate' | 'implementationEffort' | 'timeToValue'
>;

export interface DecisionBrief {
  id: string;
  rank: number;
  title: string;
  category: string;
  /** Effort pill: derived from implementationEffort, never overridden. */
  effortTag: EffortTag;
  /** Full ROI range, e.g. "$11.8M – $22.7M". */
  roi: string;
  /** Condensed ROI for list rows, e.g. "$11.8–22.7M". */
  roiShort: string;
  ttv: string;
  valueScore: number;
  implScore: number;
  totalScore: number;
  percentile: number;
  verdict: VerdictKey;
  decisionText: string;
  summary: string;
  description: string;
  steps: string[];
  context: string;
  opImpact: string;
  risks: RiskItem[];
  roadmap: RoadmapPhase[];
}

/**
 * The denominator for every "rank X of Y" and percentile claim. This is the ranked/scored set
 * size (DEMO_OPPORTUNITIES), NOT the 174-use-case reference library (MOCK_TOTAL_COUNT) — ranking
 * is computed only over these opportunities, so a rank claim against the library would assert a
 * comparison the scoring engine never made. See BACKLOG.md and libraryMapping.ts: the library and
 * this ranked set are related (every ranked opportunity now has a real library identity) but they
 * are not the same population, and "library size" and "ranked-set size" are different numbers.
 */
export const TOTAL_PORTFOLIO_SIZE = DEMO_OPPORTUNITIES.length;

type Effort = 'Low' | 'Medium' | 'High';

/** English ordinal suffix — authored copy writes "{percentile}th", which reads wrong at 93/92/91. */
const ordinal = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
};

const interpolate = (
  s: string,
  vars: { rank: number; total: number; percentile: number; value: number; impl: number; annualBenefit: string },
) =>
  s
    .replace(/\{rank\}/g, String(vars.rank))
    .replace(/\{total\}/g, String(vars.total))
    .replace(/\{percentile\}th/g, ordinal(vars.percentile))
    .replace(/\{percentile\}/g, String(vars.percentile))
    .replace(/\{value\}/g, String(vars.value))
    .replace(/\{impl\}/g, String(vars.impl))
    .replace(/\{annualBenefit\}/g, vars.annualBenefit);

/**
 * Generic structured risks by effort level, used when a record has no authored register.
 * Ported from EAUseCaseBriefPage's RISK_DATA so derived briefs read as they did before.
 */
const GENERIC_RISKS: Record<Effort, RiskItem[]> = {
  Low: [
    { name: 'Data Quality Risk', severity: 'Medium', likelihood: 'Low', description: 'Data quality inconsistencies across source systems may require remediation before full deployment. Preliminary profiling indicates completeness rates of 85–92% across key fields, within acceptable range, but targeted cleansing will be required in two source domains.', mitigation: 'Conduct a formal data quality assessment in Phase 1. Establish data stewardship protocols and define minimum quality thresholds as a go/no-go criterion before the build phase begins.', owner: 'Chief Data Officer' },
    { name: 'User Adoption Risk', severity: 'Medium', likelihood: 'Medium', description: 'End-user adoption may be slower than projected without dedicated change management and training resources. Prior analogous initiatives at comparable health systems achieved 60–70% active utilization within 90 days post-launch when supported by structured training programs.', mitigation: 'Develop a comprehensive training curriculum with role-specific content. Designate clinical champions in each department and establish a feedback loop to capture early adoption barriers.', owner: 'VP of Analytics' },
    { name: 'Vendor Dependency Risk', severity: 'Low', likelihood: 'Low', description: 'Vendor integration timelines may shift based on third-party release schedules and resource availability. Current vendor commitments are verbal; contractual SLAs have not yet been established.', mitigation: 'Maintain parallel development tracks. Define and contractually bind vendor SLAs during procurement. Include penalty provisions for schedule slippage.', owner: 'CTO / IT' },
  ],
  Medium: [
    { name: 'Integration Complexity Risk', severity: 'High', likelihood: 'Medium', description: 'EHR integration complexity may extend the implementation timeline by 4–8 weeks beyond estimate. The primary EHR vendor has indicated a 6–8 week lead time for API provisioning, which is not yet reflected in the current project schedule.', mitigation: 'Engage EHR vendor in formal discovery within the first two weeks of project initiation. Scope integration requirements precisely and model timeline impact before finalizing the project plan.', owner: 'CTO / IT' },
    { name: 'Data Governance Risk', severity: 'Medium', likelihood: 'Medium', description: 'Gaps in data governance policy may delay access to requisite clinical datasets. Current governance frameworks do not explicitly address the cross-departmental data sharing requirements of this initiative.', mitigation: 'Initiate a governance review and IRB assessment in parallel with technical build activities. Engage the Privacy Officer and Compliance team in Week 1 to map required approvals and estimated timelines.', owner: 'Chief Data Officer' },
    { name: 'Adoption & Sustainment Risk', severity: 'Medium', likelihood: 'High', description: 'Sustained adoption beyond initial go-live requires ongoing operational support, embedded training, and active monitoring. Without this infrastructure, adoption typically declines 30–40% within six months of launch.', mitigation: 'Establish an embedded analytics support function with defined escalation paths. Build sustained adoption metrics into the governance framework and review quarterly.', owner: 'VP of Analytics' },
    { name: 'Regulatory Change Risk', severity: 'Low', likelihood: 'Medium', description: 'Evolving regulatory guidance in this domain could require model recalibration or workflow adjustments after launch, adding unplanned maintenance cost.', mitigation: 'Monitor the regulatory landscape quarterly with input from Legal and Compliance. Build model refresh cycles into the annual roadmap.', owner: 'Compliance / Legal' },
  ],
  High: [
    { name: 'Systems Integration Risk', severity: 'Critical', likelihood: 'Medium', description: 'Cross-system data integration complexity poses material risk to timeline and budget. This initiative spans four source systems with divergent data models, requiring a custom integration layer that adds significant technical complexity and failure surface area.', mitigation: 'Engage a senior integration architect in Phase 1. Adopt a phased integration approach to contain blast radius. Define strict data contract standards across all upstream systems before build begins.', owner: 'CTO / IT' },
    { name: 'Change Management Risk', severity: 'High', likelihood: 'High', description: 'Organizational change management is the most significant risk. Workflow changes affect clinical and operational staff across multiple departments. Without executive commitment to change management, adoption failure is likely.', mitigation: 'Appoint a dedicated executive sponsor with resource authority. Commission a formal change impact assessment before go-live. Budget for at minimum 12 weeks of structured change management support.', owner: 'COO / CMO' },
    { name: 'Compliance & Regulatory Risk', severity: 'High', likelihood: 'Medium', description: 'Regulatory and compliance requirements are evolving and may introduce mandatory review gates that delay the timeline by 4–12 weeks. Legal exposure is elevated if not addressed proactively.', mitigation: 'Schedule a dedicated compliance review gate in Stage 2. Engage legal counsel in the discovery phase to map all applicable regulations and required approval pathways.', owner: 'Compliance / Legal' },
    { name: 'Data Readiness Risk', severity: 'Medium', likelihood: 'Medium', description: 'Data quality remediation across multiple source systems is a hard prerequisite for model accuracy. Current profiling indicates completeness gaps in 3 of 6 required source domains, with estimated remediation effort of 6–8 weeks.', mitigation: 'Allocate dedicated data engineering resources for pre-implementation data preparation. Establish data quality SLAs for each source system and validate before moving to build.', owner: 'Chief Data Officer' },
    { name: 'Vendor Scalability Risk', severity: 'Medium', likelihood: 'Low', description: 'Vendor ecosystem maturity varies. Early-rollout results from early-adopter health systems may not generalize at enterprise scale for systems with higher patient volumes or more complex payer mixes.', mitigation: 'Conduct formal vendor evaluation including reference checks from comparable health systems. Require performance guarantees for the initial rollout contractually.', owner: 'VP of Analytics' },
  ],
};

const genericRoadmap = (effort: Effort): RoadmapPhase[] => {
  const spans: Record<Effort, [string, string, string]> = {
    Low: ['Weeks 1–4', 'Weeks 5–12', 'Months 4–6'],
    Medium: ['Weeks 1–6', 'Months 2–6', 'Months 7–12'],
    High: ['Months 1–3', 'Months 4–9', 'Months 10–18'],
  };
  const [d1, d2, d3] = spans[effort];
  return [
    { phase: 'Foundation', dur: d1, desc: 'Data readiness assessment, governance approvals, and steering committee stand-up.' },
    { phase: 'Limited rollout', dur: d2, desc: 'Controlled limited rollout against a defined cohort, measured on baseline KPIs with an adoption feedback loop.' },
    { phase: 'Scale', dur: d3, desc: 'Enterprise rollout with support model in place and ROI tracking live for Finance and Operations.' },
  ];
};

/**
 * Pure derivation from real fixture fields. Score and percentile formulas are carried over
 * verbatim from EAUseCaseBriefPage's buildProfileFromOpportunity so displayed scores do not
 * shift for existing users.
 */
export function deriveBrief(opp: BriefSource, rank: number): DecisionBrief {
  const effort = (opp.implementationEffort || 'Medium') as Effort;
  const effortBonus = { Low: 12, Medium: 0, High: -10 }[effort];

  // Featured opps take their scores from the authored root (single source); the rank-derived
  // approximation is retained only as the fallback for non-featured fillers.
  const rootScore = scoreForUseCase(OPP_TO_UC[opp.id]);
  const valueScore = rootScore ? Math.round(rootScore.value) : Math.round(Math.max(52, Math.min(93, 89 - rank * 1.4)));
  const implScore = rootScore ? Math.round(rootScore.impl) : Math.round(Math.max(42, Math.min(90, 74 + effortBonus - rank * 0.7)));
  const totalScore = rootScore ? Math.round(rootScore.composite) : Math.round(valueScore * 0.55 + implScore * 0.45);
  // Genuine percentile against the ranked set this opportunity was actually ranked within —
  // NOT an authored curve. (The prior formula, 99 - (rank-1)*1.9 floored at 58, was tuned to
  // read plausibly for a 174-item denominator and diverged from a real percentile past rank ~10.)
  const percentile = Math.round(((TOTAL_PORTFOLIO_SIZE - rank) / TOTAL_PORTFOLIO_SIZE) * 100);

  // ROI range comes from the ENGINE, not from roiEstimate directly. Formatting
  // the fixture here produced a figure that drifted from the ROI Model page and
  // the approval PDFs by a rounding step and by the engine's ±10% band.
  const engineModel = roiForUseCase(opp.id);
  const roiLow = engineModel.annualValueRangeLow / 1_000_000;
  const roiHigh = engineModel.annualValueRangeHigh / 1_000_000;
  const roi = `$${roiLow.toFixed(1)}M – $${roiHigh.toFixed(1)}M`;
  const roiShort = `$${roiLow.toFixed(1)}–${roiHigh.toFixed(1)}M`;
  // NOTE: the executive summary below quotes hubFigures (Base Case), the same call the hub header
  // tile renders, not `roi` (conservative to aggressive), which only feeds brief.roi list rows.
  const ttv = (opp.timeToValue || '6–12 months').replace(/-/g, '–');

  // Thresholds carried over from the page's former PROCEED/EVALUATE/DEFER recommendation,
  // so verdicts match what these records already showed.
  //
  // TODO: calibrate against real scoring data. Because valueScore falls linearly with rank,
  // these cutoffs skew the catalog hard toward 'hold': across the 74 fixture records at their
  // natural ranks the spread is green 6 / cond 20 / hold 48, with everything past ~rank 25
  // deriving to 'hold'. That is faithful to today's behaviour but almost certainly not the
  // real distribution — revisit once the scoring model supplies verdicts directly.
  const verdict: VerdictKey = totalScore >= 72 ? 'green' : totalScore >= 55 ? 'cond' : 'hold';
  const priorityLabel = rank <= 5 ? 'Tier 1 priority' : rank <= 15 ? 'strategic fit' : 'future consideration';
  const category = opp.category;

  const decisionText =
    `This initiative demonstrates ${valueScore >= 75 ? 'strong' : 'moderate'} business value ` +
    `and ${implScore >= 65 ? 'favorable' : 'manageable'} implementation feasibility across the four-dimension composite. ` +
    (verdict === 'green'
      ? 'ROI fundamentals are defensible at the CFO level, organizational readiness is sufficient for a phased launch, and strategic alignment is confirmed across CDO and operational leadership. Ready for resource commitment and board-level approval.'
      : verdict === 'cond'
        ? 'Key assumptions underpinning the ROI model require validation through a structured feasibility phase. Additional discovery on data readiness and integration complexity is recommended before committing full implementation resources.'
        : 'This initiative should be revisited in the next planning cycle as organizational readiness and strategic priorities evolve. Current constraints make near-term execution unlikely to achieve projected returns.');

  const summary =
    `${opp.title} is a ${priorityLabel} analytics initiative within the ${category} domain. ` +
    `Projected annual benefit of ${hubFigures(opp.id).annualBenefitRange} with a ${ttv} time-to-value trajectory positions this at rank ` +
    `#${rank} of ${TOTAL_PORTFOLIO_SIZE} ranked, ${ordinal(percentile)} percentile. ` +
    (verdict === 'green'
      ? 'All key investment criteria are met.'
      : verdict === 'cond'
        ? 'Further feasibility evaluation is warranted prior to full commitment.'
        : 'Recommend deferring to the next strategic planning cycle.');

  const context =
    `The ${category} domain represents one of the highest-priority investment areas for health systems ` +
    `navigating the transition to value-based care. Peer organizations investing in analytics capabilities ` +
    `in this area report average performance improvement of 12–18% in targeted quality metrics within ` +
    `18 months of deployment. This initiative builds reusable infrastructure that compounds value across ` +
    `future portfolio use cases.`;

  const opImpact =
    `Expected to reduce manual effort by 15–25% in targeted workflows and accelerate decision cycle times ` +
    `through real-time data availability. Early efficiency gains are achievable within the first 6 months ` +
    `of deployment, with full run-rate impact realized by month 12.`;

  const steps =
    verdict === 'hold'
      ? [
          'Document the readiness gaps blocking near-term execution.',
          'Track prerequisite capability build in the enterprise data roadmap.',
          'Re-score this opportunity at the next planning cycle.',
        ]
      : [
          'Assign an executive sponsor and constitute a cross-functional steering committee (CDO, IT, Finance, Operations).',
          'Commission a data readiness assessment and integration scoping exercise; target completion 2 weeks.',
          verdict === 'cond'
            ? 'Validate the ROI model’s key assumptions through a structured feasibility phase before releasing build funding.'
            : 'Issue an RFI to qualified implementation partners; shortlist by end of quarter.',
        ];

  return {
    id: opp.id,
    rank,
    title: opp.title,
    category,
    // Quadrant rule (single Quick Win definition): featured opps resolve their real value/feas
    // quadrant via id; fillers fall back to the rank-derived value/impl axes. Retires Low+≤6mo.
    effortTag: isQuickWin({ id: opp.id, bvAxis: valueScore, ieAxis: implScore }) ? 'Quick Win' : 'Moderate',
    roi,
    roiShort,
    ttv,
    valueScore,
    implScore,
    totalScore,
    percentile,
    verdict,
    decisionText,
    summary,
    description: opp.description,
    steps,
    context,
    opImpact,
    risks: GENERIC_RISKS[effort],
    roadmap: genericRoadmap(effort),
  };
}

const cache = new Map<string, DecisionBrief>();

/**
 * The only brief accessor pages should call. Derives, then layers authored overrides,
 * interpolating rank-dependent tokens in the authored copy.
 */
export function getDecisionBrief(opp: BriefSource, rank: number): DecisionBrief {
  const key = `${opp.id}:${rank}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const base = deriveBrief(opp, rank);
  const override = BRIEF_OVERRIDES[opp.id];

  let brief = base;
  if (override) {
    const vars = {
      rank,
      total: TOTAL_PORTFOLIO_SIZE,
      percentile: base.percentile,
      value: base.valueScore,
      impl: base.implScore,
      // The same base-scenario range the hub header tile renders; one source for both.
      annualBenefit: hubFigures(opp.id).annualBenefitRange,
    };
    brief = {
      ...base,
      verdict: override.verdict,
      decisionText: interpolate(override.decisionText, vars),
      summary: interpolate(override.summary, vars),
      steps: override.steps,
      context: override.context,
      opImpact: override.opImpact,
      risks: override.risks,
      roadmap: override.roadmap,
    };
  }

  cache.set(key, brief);
  return brief;
}
